/**
 * The permission editor's rules, mirroring normalizePermissions on the server
 * (backend/src/utils/permissions.js) so what the owner ticks is what gets
 * saved. The server applies them again and remains the authority.
 *
 * `catalog` is what GET /api/shops/current/permissions returns.
 */
const SEE_COSTS = "power.see_costs";

const areaOf = (catalog, area) => catalog.areas.find((entry) => entry.area === area);

/** Ticking or unticking one box, with what it implies. */
export const togglePermission = (catalog, current, permission) => {
  const granted = new Set(current);
  const [scope, action] = permission.split(".");
  const turningOn = !granted.has(permission);

  if (scope === "power") {
    if (turningOn) {
      granted.add(permission);
    } else {
      granted.delete(permission);
      // A money-only area cannot stay without the buy prices it is made of
      if (permission === SEE_COSTS) {
        catalog.areas
          .filter((entry) => entry.needs_costs)
          .forEach((entry) =>
            entry.actions.forEach((name) => granted.delete(`${entry.area}.${name}`)),
          );
      }
    }
    return orderLike(catalog, granted);
  }

  const entry = areaOf(catalog, scope);

  if (turningOn) {
    granted.add(permission);
    // Editing a screen you cannot open is not a permission anyone means
    granted.add(`${scope}.view`);
    if (entry?.needs_costs) granted.add(SEE_COSTS);
  } else if (action === "view") {
    // No longer seeing a screen means no longer acting on it either
    entry?.actions.forEach((name) => granted.delete(`${scope}.${name}`));
  } else {
    granted.delete(permission);
  }

  return orderLike(catalog, granted);
};

/** The same canonical order the server stores, so a list compares equal. */
const orderLike = (catalog, granted) =>
  [
    ...catalog.areas.flatMap((entry) =>
      entry.actions.map((action) => `${entry.area}.${action}`),
    ),
    ...catalog.powers.map((power) => `power.${power}`),
  ].filter((permission) => granted.has(permission));

/** The preset these permissions are exactly, or null when customised. */
export const matchingPreset = (catalog, permissions) => {
  const key = [...permissions].sort().join("|");

  return (
    Object.entries(catalog.presets).find(
      ([, list]) => [...list].sort().join("|") === key,
    )?.[0] || null
  );
};
